import { Component, useEffect } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { useRouteError } from 'react-router'
import { Button } from '@/components/ui/button'
import { reportClientError } from '@/api/report'

/**
 * The recoverable fallback every mechanism in this file (and
 * `routes.tsx`'s `lazyArea` helper) renders — "Something went wrong on this
 * screen" plus a way forward (task-12-brief.md's own words).
 *
 * EXPORTED (not private): `routes.tsx`'s `lazyArea` also renders this
 * directly, for a failure class neither mechanism below actually covers —
 * see that file's own top comment for the measured reason. Both of this
 * component's call sites (the class boundary below, and `lazyArea`) already
 * reported what broke before rendering it; this file does not enforce that
 * pairing structurally (there is no type that could), but every caller does
 * it, and a reviewer changing that should re-read why (review §5.5 — a
 * white screen with no telemetry is the failure being closed).
 *
 * NOT rendered by `RouteErrorFallback` below, on purpose — review round 2's
 * Important 1 found that sharing this component there was actively unsafe.
 * See that function's own doc comment for the measured reason and the fix.
 *
 * `window.location.reload()`, not `navigate(...)`: a full reload re-fetches
 * the JS bundle and re-runs every module's top-level code from scratch,
 * which a client-side `navigate` does not — if the crash came from bad
 * in-memory state (a corrupted cache entry, a stuck subscription), only a
 * real reload is guaranteed to clear it. Styled like `NotFoundScreen`
 * (`AppShell.tsx`) for a consistent "something's wrong, here's the way
 * back" family of screens, not a one-off.
 */
export function Fallback() {
  return (
    <div className="mx-auto flex min-h-[50vh] max-w-sm flex-col items-center justify-center gap-3 p-6 text-center">
      <h1 className="text-xl font-semibold">Something went wrong on this screen</h1>
      <p className="text-sm text-muted-foreground">
        The error has been reported. Reloading usually fixes it.
      </p>
      <Button variant="outline" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </div>
  )
}

/**
 * The class half of this task's two mechanisms — see `routes.tsx`'s own top
 * comment for the full, measured account of how the two divide
 * responsibility (and a third case, lazy-import failure, that turned out to
 * belong to neither and is handled in `routes.tsx` directly). A React error
 * boundary can ONLY be a class component — `componentDidCatch`/
 * `getDerivedStateFromError` have no hook equivalent as of React 19, which
 * is why this is the one class component in `packages/web/src`.
 *
 * Wraps `AppShell` itself and each of its area children's RESOLVED
 * component (`routes.tsx`) — every one of them independently, not once
 * around the whole tree. That matters: because React finds the NEAREST
 * ancestor boundary in the actual component tree (not the nearest in some
 * notion of "route hierarchy"), an `ErrorBoundary` wrapping just
 * `PipelineArea` catches a throw from `PipelineArea` BEFORE it ever reaches
 * the `ErrorBoundary` wrapping `AppShell` one level up — so a bug in one
 * area shows this fallback in the content pane while the nav and header
 * (rendered by `AppShell`, above the failure) stay on screen and usable.
 * CONFIRMED LIVE (task report): a forced throw inside a mounted area left
 * "Pipeline"/"Money"/"Site ops" nav links and the "Sign out" button all
 * still present and queryable while the content pane alone showed this
 * fallback.
 *
 * `componentDidCatch` reports and nothing else — no retry, no error
 * counting, no rate limiting. `getDerivedStateFromError` is what actually
 * switches the render output to the fallback; the two are separate
 * lifecycle methods for a reason (React's own docs: `getDerivedStateFromError`
 * must be pure, so any side effect — a network call — belongs in
 * `componentDidCatch`, which runs after render commits).
 *
 * THE MULTIPLIER IS REAL, not merely a theoretical gap in "no rate
 * limiting" above: review round 2 measured React retrying a render that
 * threw — once synchronously before finally committing to the error state
 * — for BOTH this class boundary and `RouteErrorFallback` below, recording
 * 3 and 5 fallback renders for what was, underneath, one logical failure.
 * Since `componentDidCatch` fires on every commit that lands in the error
 * state, one real crash can file more than one report. Left AS a real,
 * accepted cost rather than de-duplicated here: Workers Logs is a log, not
 * an alerting product with its own dedup (that gap is exactly what a real
 * Sentry-class tool would buy — see `worker.ts`'s own doc comment on why
 * that trade was made anyway), and a handful of duplicate log lines for a
 * genuine crash is a far cheaper failure mode than adding de-duplication
 * logic to the one code path that runs precisely when something has
 * already gone wrong.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  override state = { hasError: false }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true }
  }

  override componentDidCatch(error: unknown, _info: ErrorInfo): void {
    reportClientError(error, { route: window.location.pathname, kind: 'boundary' })
  }

  override render(): ReactNode {
    if (this.state.hasError) return <Fallback />
    return this.props.children
  }
}

/**
 * The `errorElement` half — a FUNCTION component, because `errorElement`
 * does not use React's class-boundary protocol at all: react-router
 * maintains its OWN error state per route (`useRouteError()` reads it via
 * context) and renders whatever element a route names, independent of
 * `componentDidCatch`.
 *
 * Wired ONLY on routes that declare no `lazy` of their own (`/login`,
 * `/set-password`, `/` and `/no-areas` — see `routes.tsx`'s own top comment
 * for the measured reason `errorElement` is deliberately ABSENT from every
 * `lazy` route). For those routes, this is a genuine, WORKING defense-in-
 * depth net for whatever a class `ErrorBoundary` structurally cannot catch
 * — an error thrown by the boundary's OWN fallback render (a boundary
 * cannot catch an error from its own `render`), or a future `loader`/
 * `action` throwing (none of this app's routes declare either today, so
 * that half is inert, not exercised).
 *
 * "GENUINE, WORKING" is a claim this file previously made and did not back
 * up — review round 2's Important 1, measured three ways. This component
 * used to `return <Fallback />` (the same component `ErrorBoundary` renders
 * above). React-router's own `RenderErrorBoundary`
 * (`react-router/dist/development/lib/hooks.js`) is the class boundary that
 * renders WHATEVER `errorElement` names, and for the four routes this is
 * wired on, it sits ABOVE every other mechanism in this file in the React
 * tree — there is nothing further out to catch a SECOND throw from what it
 * renders. Sharing `<Fallback />` here meant that if the bug reaching this
 * point originated INSIDE `Fallback` (or `Button`, or `cn`, or anything
 * either transitively imports), rendering the identical buggy component
 * again threw again, with nothing left to catch it — measured directly:
 * `document.body.innerHTML` collapsed to an empty `<div></div>`, a blank
 * page, after React's own retry-before-giving-up behaviour (see the class
 * boundary's own comment on the multiplier) ran out. Removing `errorElement`
 * entirely, for comparison, produced react-router's OWN ugly default
 * screen instead — worse than this component's normal fallback, but still
 * not blank. Sharing the visually-richer `Fallback` therefore made the ONE
 * scenario this component exists for STRICTLY WORSE than not having it at
 * all.
 *
 * FIXED by rendering RAW, IMPORT-FREE markup below instead: plain DOM
 * elements (`div`/`h1`/`p`/`button`) and static Tailwind class STRINGS, no
 * component import, no shared logic with `Fallback` — nothing left inside
 * this render that CAN throw. Slightly duplicated markup with `Fallback`
 * above is the accepted cost of that guarantee; a shared sub-component
 * would just relocate the same single-point-of-failure this fix removes.
 *
 * `kind: 'boundary'`, the same as the class component above — task-12-
 * brief.md's own interface names exactly three kinds (`'boundary' |
 * 'window' | 'rejection'`), and a fourth purely to distinguish "which
 * mechanism caught this" would tell Workers Logs something about this
 * app's internals, not about the failure a reader of the logs cares about.
 */
export function RouteErrorFallback() {
  const error = useRouteError()
  // `useEffect`, not a direct call in the render body: this IS a render
  // function (unlike the class boundary's `componentDidCatch`, a lifecycle
  // method reserved for effects), and `<StrictMode>` (`main.tsx`) double-
  // invokes a component's render in development specifically to surface an
  // impure one — a direct call here would file two reports per real error in
  // dev. `useEffect` runs once per commit, matching the class boundary's own
  // one-report-per-catch behaviour exactly.
  useEffect(() => {
    reportClientError(error, { route: window.location.pathname, kind: 'boundary' })
  }, [error])
  return (
    <div className="mx-auto flex min-h-[50vh] max-w-sm flex-col items-center justify-center gap-3 p-6 text-center">
      <h1 className="text-xl font-semibold">Something went wrong on this screen</h1>
      <p className="text-sm text-muted-foreground">
        The error has been reported. Reloading usually fixes it.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="inline-flex h-9 items-center justify-center rounded-md border bg-background px-4 text-sm font-medium shadow-xs hover:bg-accent"
      >
        Reload
      </button>
    </div>
  )
}
